import { Suspense, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { LiveTicker } from "@/components/market/LiveTicker";
import { useCommunityLinks } from "@/hooks/useCommunityLinks";
import { useSeo } from "@/hooks/useSeo";

const LandingBelow = lazy(() => import("@/components/LandingBelow"));

function useAfterPaint() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    const id = window.requestIdleCallback ? window.requestIdleCallback(go, { timeout: 800 }) : setTimeout(go, 120);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);
  return ready;
}

export default function Landing({ scrollTo }) {
  useSeo();
  const links = useCommunityLinks();
  const location = useLocation();
  const ready = useAfterPaint();
  useEffect(() => {
    if (location.state?.authError) toast.error("Sign-in didn't complete. Please try again.");
    const id = location.state?.scrollTo;
    if (!id) return undefined;
    let tries = 0, last = -1, done = false;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      const top = el ? Math.round(el.getBoundingClientRect().top + window.scrollY) : -1;
      if (done) { if (el && top !== last) scrollTo?.(id, true); last = top; if (++tries > 28) clearInterval(timer); return; }
      if (el && top === last) { scrollTo?.(id); done = true; return; }
      last = top;
      if (++tries > 16) { if (el) { scrollTo?.(id); done = true; } else clearInterval(timer); }
    }, 250);
    return () => clearInterval(timer);
  }, [location.state, scrollTo]);
  return <main className="site-shell">
    <div className="top-disclaimer" data-testid="top-disclaimer"><ShieldCheck size={15} /> Learning only · Not investment advice · Not SEBI registered</div>
    <Nav onAnchor={scrollTo} />
    <LiveTicker />
    <Hero links={links} />
    {ready ? <Suspense fallback={<div className="below-fold-placeholder" aria-hidden="true" />}><LandingBelow links={links} /></Suspense> : <div className="below-fold-placeholder" aria-hidden="true" />}
  </main>;
}
